import React from 'react'

// Constants
import { routes } from '@sketch/constants'

// Hooks
import {
  BaseResponsiveDropdownProps,
  useResponsiveDropdown,
} from 'components/ResponsiveDropdown'

// Components
import { BaseButtonItem, LinkItem } from 'modules/projects/components/Item'

// Local Icons
import { icons } from '../../icons'

// Styles
import { Chevron } from '../BottomMenu/BottomMenu.styles'
import {
  DropdownHeader,
  DropdownWrapper,
  DropdownItem,
} from '../HelpMenu/HelpMenu.styles'

// Types
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { useIsProfileEnabled } from 'modules/workspaceProfile/hooks/useIsProfileEnabled'

interface SettingsMenuProps {
  workspace: WorkspaceMinimalFragment
  permissions: {
    isAdmin: boolean
    showWorkspaceSettingsBilling: boolean
    showWorkspaceSettingsGeneral: boolean
  }
}

interface SettingsMenuContentProps extends BaseResponsiveDropdownProps {
  showWorkspaceSettingsBilling?: boolean
  showWorkspaceSettingsGeneral?: boolean
  isAdmin?: boolean
  workspaceId: WorkspaceMinimalFragment['identifier']
}
/**
 *
 * SettingsMenuContent
 *
 * Renders the content for the "SettingsMenu" menu used on the "Sidebar"
 */
const SettingsMenuContent: React.FC<SettingsMenuContentProps> = ({
  workspaceId,
  showWorkspaceSettingsBilling,
  showWorkspaceSettingsGeneral,
  isAdmin,
  currentVariant,
}) => {
  const { isProfileEnabled } = useIsProfileEnabled(workspaceId)
  const isModalVariant = currentVariant === 'modal'

  return (
    <DropdownWrapper>
      {isModalVariant && <DropdownHeader>Settings</DropdownHeader>}
      <DropdownItem>
        <LinkItem
          icon={icons.EmailIcon}
          text="People"
          to={routes.WORKSPACE_SETTINGS_PEOPLE.create({ workspaceId })}
        />
      </DropdownItem>
      {showWorkspaceSettingsGeneral && (
        <DropdownItem>
          <LinkItem
            icon={icons.GearIcon}
            text="General"
            to={routes.WORKSPACE_SETTINGS_SETTINGS.create({ workspaceId })}
          />
        </DropdownItem>
      )}
      {isProfileEnabled && isAdmin && (
        <LinkItem
          icon={icons.ControlIcon}
          text="Profile"
          to={routes.WORKSPACE_SETTINGS_PROFILE.create({ workspaceId })}
        />
      )}
      {showWorkspaceSettingsBilling && (
        <DropdownItem>
          <LinkItem
            icon={icons.Money}
            text="Billing"
            to={routes.WORKSPACE_SETTINGS_BILLING.create({ workspaceId })}
          />
        </DropdownItem>
      )}
    </DropdownWrapper>
  )
}

/**
 *
 * SettingsMenu
 *
 * Renders the Settings menu used in the "BottomMenu" on the "Sidebar"
 *
 */
const SettingsMenu: React.FC<SettingsMenuProps> = ({
  workspace,
  permissions,
}) => {
  const workspaceId = workspace.identifier

  const {
    showWorkspaceSettingsBilling,
    showWorkspaceSettingsGeneral,
    isAdmin,
  } = permissions

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: SettingsMenuContent,
    dropdownProps: {
      workspaceId,
      showWorkspaceSettingsBilling,
      showWorkspaceSettingsGeneral,
      isAdmin,
    },
    placement: 'right',
  })

  return (
    <>
      <BaseButtonItem
        icon={icons.GearIcon}
        text="Settings"
        additional={<Chevron />}
        {...buttonProps}
      />
      {content}
    </>
  )
}

export default SettingsMenu
